/*!
 * Floppy, uses Dexie (V. 3.0.1), A Minimalistic Wrapper for IndexedDB
 * Authors: ['Jorge Brunal Perez (aka Diniremix)', 'Duverney Ortiz']
 * Version: 2020-11-05
 * https://git.sitimapa.co/serviforms/serviforms-admin
 * Released under the MIT License.
 */

import Dexie from "dexie";
import DateUtils from "@/helpers/DateUtils.js";

export default {
  db: null,

  init({ dbName, versionNumber, schema }) {
    console.info("fn init");
    console.info("dbName:", dbName);
    console.info("versionNumber:", versionNumber);
    console.info("schema:", schema);

    this.db = new Dexie(dbName);
    this.db.version(versionNumber).stores(schema);

    this.db.open().catch(function (e) {
      console.error("========================================================");
      console.error("IndexedDB cannot be used in this browser!.");
      console.error("Floppy cannot use IndexedDB.");
      console.error("More information at: https://caniuse.com/#feat=indexeddb");
      console.error("IndexedDB", e.name, e.message);
      console.error("========================================================");
    });
  },

  open() {
    return new Promise((resolve, reject) => {
      try {
        resolve(this.db.open());
      } catch (error) {
        reject(error);
      }
    });
  },

  close() {
    this.db.close();
  },

  isOpen() {
    return this.db.isOpen();
  },

  getAll(table) {
    const myTable = this.db.table(table);
    return myTable.toArray();
  },

  getKey(table, key) {
    const myTable = this.db.table(table);
    return myTable.get(key);
  },

  generateRandomKey() {
    return Math.random().toString(36).substring(2, 34) + Math.random().toString(36).substring(2, 34);
  },

  generateId() {
    // thanks to: https://github.com/firebase/firebase-js-sdk
    // branch: /73a586c92afe3f39a844b2be86086fddb6877bb7/packages/firestore/src/util/misc.ts

    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let autoId = "";
    const maxChar = 20;

    for (let i = 0; i < maxChar; i++) {
      autoId += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    if (!(autoId.length === maxChar)) {
      return new Error(`Invalid auto ID: ${autoId}`);
    }

    return autoId;
  },

  save(table, data, key = null) {
    if (!key) key = this.generateRandomKey();
    const myTable = this.db.table(table);
    var copyData = Object.assign({}, data, {
      key: key,
      date_create: data.date_create ? data.date_create : DateUtils.timeStamp()
    });
    return myTable.add(copyData, key);
  },

  put(table, data, key) {
    const myTable = this.db.table(table);
    return myTable.put(data, key);
  },

  update(table, data, key) {
    const myTable = this.db.table(table);
    return myTable.update(key, data);
  },

  updateStateForm(table) {
    const myTable = this.db.table(table);
    myTable.toCollection().modify(form => {
      form.active = false;
    });
  },

  deleteKey(table, key) {
    const myTable = this.db.table(table);
    return myTable.delete(key);
  },

  count(table) {
    const myTable = this.db.table(table);
    return myTable.count();
  },

  limit(table, count) {
    const myTable = this.db.table(table);
    return myTable.limit(count).toArray();
  },

  first(table, key) {
    const myTable = this.db.table(table);
    return myTable.toCollection().first();
  },

  last(table, key) {
    const myTable = this.db.table(table);
    return myTable.toCollection().last();
  },

  getTableInstance(table) {
    const myTable = this.db.table(table);
    return new Promise((resolve, reject) => {
      if (myTable) {
        resolve(myTable);
      } else {
        reject(myTable);
      }
    });
  },

  getDBInstance() {
    return new Promise((resolve, reject) => resolve(this.db));
  },

  schema() {
    return new Promise((resolve, reject) => {
      if (this.db.isOpen()) {
        const currentSchema = this.db.tables.map(({ name, schema }) => {
          // console.log("name:", name);
          // console.log("schema:", schema);
          return { name, schema };
        });
        resolve(currentSchema);
      } else {
        reject("DatabaseClosedError: Database has been closed");
      }
    });
  },

  truncate(table, confirm) {
    const myTable = this.db.table(table);
    return myTable.clear();
  },

  async truncateFilter(table, query) {
    /*
       query: {
        ['nombre del campo de la tabla']: ['valor condicion']
       }
        */
    const myTable = await this.db.table(table).where(query);
    return myTable.delete();
  },

  //Giovanni Rosas
  giovanni(dbname) {
    this.db.close();
    // this.db.delete()
    console.warn("Giovanni Rosas has deleted the database!: ¯\\_(ツ)_/¯");
    return Dexie.delete(dbname);
  },

  deleteDB(dbname, confirm) {
    return new Promise((resolve, reject) => {
      if (confirm !== null && confirm !== undefined) {
        if (confirm === "Yes!") {
          this.db.close();
          // this.db.delete()
          resolve(Dexie.delete(dbname));
        }
        reject("DatabaseError: the database cannot be deleted, the order must be: Yes!");
      }
      reject("DatabaseError: the database cannot be deleted, confirm your order first!");
    });
  },

  dbname() {
    return this.db.name;
  },

  version() {
    return this.db.verno;
  },

  async filter_(table, options) {
    const resultData = [];
    const condition = `${options.operation} ${options.condition}`;
    const filter = await this.db.table(table).filter(item => eval(item[options.field] + condition));
    await filter.each(element => resultData.push(element));
    return resultData;
  },

  async where_order_sortBy(table, query, byOrder) {
    const myTable = this.db.table(table);
    const resultData = [];
    const result = await myTable.where(query).sortBy(byOrder);
    // await myTable.orderBy(byOrder).each((element) => resultData.push(element));
    return result;
  },

  async where_(table, query, options) {
    const myTable = this.db.table(table);
    const whereCond = myTable.where(query);
    switch (options.query) {
      case "first": {
        const result = await whereCond.first();
        return result;
        break;
      }
      case "each": {
        const resultData = [];
        await whereCond.each(element => resultData.push(element));
        return resultData;
        break;
      }
      default: {
        console.error("where_ error: invalid option:", options.query);
        reject(`error: invalid option: ${options.query}`);
        break;
      }
    }
  }
};
